"use client"

import { useMemo } from "react"
import { useAuthStore } from "@/stores/auth-store"

export interface UsePermissionsResult {
  /** True once the auth `/me` call has resolved (user !== null). */
  isLoaded: boolean
  /** The current user's Spatie permission strings (empty while loading). */
  permissions: string[]
  /** Holds the single permission. Always false while `!isLoaded`. */
  can: (permission: string) => boolean
  /** Holds ANY of the permissions. Always false while `!isLoaded`. */
  canAny: (permissions: string[]) => boolean
  /** Holds ALL of the permissions. Always false while `!isLoaded`. */
  canAll: (permissions: string[]) => boolean
}

/**
 * Central permission hook — the single source of truth for "can this user do X"
 * across the app. Subscribes once to the auth store's `user` and derives a
 * membership Set + the three check closures inside ONE useMemo so the returned
 * object is referentially stable between renders (safe to spread into deps).
 *
 * Hide-during-hydration contract: while the user is still resolving
 * (`user === null`), `isLoaded` is false and every check returns false. This
 * means permission-gated UI (buttons, menu items) appears only once the grant
 * is confirmed — never a flash of a forbidden control that then disappears.
 */
export function usePermissions(): UsePermissionsResult {
  const user = useAuthStore((s) => s.user)

  return useMemo<UsePermissionsResult>(() => {
    const isLoaded = user !== null
    const permissions = user?.permissions ?? []
    const set = new Set(permissions)

    const can = (permission: string) => isLoaded && set.has(permission)
    const canAny = (perms: string[]) => isLoaded && perms.some((p) => set.has(p))
    const canAll = (perms: string[]) => isLoaded && perms.every((p) => set.has(p))

    return { isLoaded, permissions, can, canAny, canAll }
  }, [user])
}
